import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChatBubbleLeftIcon, MegaphoneIcon, PlusIcon } from '@heroicons/react/24/outline';
import { boardAPI } from '../../services/api';
import type { BoardPost, BoardPermissions } from '../../types/board';
import BoardPostForm from './BoardPostForm';

interface EventBoardPostsProps {
  eventId: number;
  /** A doboz stílusa (a családi nézet fehér kártyát kér). */
  className?: string;
  /** A doboz fölé kerül, de csak ha van mit mutatni. */
  heading?: React.ReactNode;
}

/**
 * Az eseményhez kötött üzenőfal-bejegyzések az esemény oldalán. Aki írhat a
 * falra, innen rögtön az eseményhez kötött bejegyzést kezdhet. Ha nincs
 * bejegyzés és írni sem lehet, nem jelenik meg.
 */
const EventBoardPosts: React.FC<EventBoardPostsProps> = ({
  eventId,
  className = 'mb-6 rounded-lg bg-gray-50 p-4',
  heading,
}) => {
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [permissions, setPermissions] = useState<BoardPermissions | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setPosts(await boardAPI.getPosts({ eventId }));
    } catch {
      setPosts([]);
    }
  }, [eventId]);

  useEffect(() => {
    load();
    boardAPI
      .getPermissions()
      .then(setPermissions)
      .catch(() => setPermissions(null));
  }, [load]);

  const canPost = permissions?.canPost ?? false;
  if (posts.length === 0 && !canPost) return null;

  return (
    <>
      {heading}
      <div className={className}>
        <div className={`mb-2 flex flex-wrap items-center gap-2 ${heading ? 'justify-end' : 'justify-between'}`}>
          {/* Ha kívül már van cím, itt nem ismételjük */}
          {!heading && (
            <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <MegaphoneIcon className="h-5 w-5 text-gray-500" />
              Üzenőfal
            </h4>
          )}
          {canPost && (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50"
            >
              <PlusIcon className="h-4 w-4" />
              Bejegyzés ehhez az eseményhez
            </button>
          )}
        </div>

        {posts.length === 0 ? (
          <p className="text-sm text-gray-500">Még nincs az eseményhez kötött bejegyzés.</p>
        ) : (
          <ul className="space-y-2">
            {posts.map((post) => (
              <li key={post.id}>
                <Link
                  to={`/board/${post.id}`}
                  className="block rounded-lg bg-white p-3 ring-1 ring-gray-200 transition hover:ring-primary-300"
                >
                  <p className="line-clamp-3 whitespace-pre-wrap break-words text-sm text-gray-900">{post.content}</p>
                  <p className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-medium text-gray-700">{post.authorName}</span>
                    <span>{new Date(post.createdAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}</span>
                    {post.commentCount > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
                        {post.commentCount}
                      </span>
                    )}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <BoardPostForm
          isOpen={formOpen}
          onClose={() => setFormOpen(false)}
          permissions={permissions}
          initialEventId={eventId}
          onSaved={load}
        />
      </div>
    </>
  );
};

export default EventBoardPosts;
